import { Mail, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { publicPaths } from '../../routes/paths';
import { usePublicSiteContent } from '../../services/public-queries';
import { CLUB_EMAIL, SocialButtons } from './SocialLinks';

export const CLUB = {
  name: 'HBAC',
  fullName: 'HUST Business Analytics Club',
  tagline: 'Data speak, we listen',
  email: CLUB_EMAIL,
  address: 'Đại học Bách khoa Hà Nội, số 1 Đại Cồ Việt, Hai Bà Trưng, Hà Nội',
  /** The address as shown in the footer: campus, then street. */
  addressLines: ['Đại học Bách khoa Hà Nội', 'Số 1 Đại Cồ Việt, Hai Bà Trưng, Hà Nội'],
} as const;

const LINKS = [
  { to: { pathname: publicPaths.home, hash: '#about' }, label: 'Về HBAC' },
  { to: { pathname: publicPaths.home, hash: '#activities' }, label: 'Hoạt động' },
  { to: publicPaths.news, label: 'Tin tức' },
  { to: publicPaths.competition, label: 'Cuộc thi HBAAC' },
];

const HEADING = 'mb-4 text-xs font-bold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400';
const LINK = 'hover:text-brand-700 dark:hover:text-brand-400';

export function PublicFooter() {
  const { content } = usePublicSiteContent();
  // Recruitment is listed only while a round is announced, as in the header.
  const links = content?.recruitment.enabled ? [...LINKS, { to: publicPaths.join, label: 'Tuyển thành viên' }] : LINKS;
  return (
    <footer id="contact" className="border-t border-slate-200 bg-slate-50 dark:border-navy-800 dark:bg-navy-950">
      <div className="mx-auto grid w-full max-w-[1328px] gap-10 px-4 py-12 md:grid-cols-12 md:gap-8 md:px-6 lg:py-14">
        <div className="md:col-span-12 lg:col-span-5">
          <div className="flex items-center gap-3">
            <img src="/logo/ava.webp" alt="" width={44} height={44} loading="lazy" decoding="async" className="h-11 w-11 rounded-full dark:hidden" />
            <img src="/logo/ava-white.webp" alt="" width={44} height={44} loading="lazy" decoding="async" className="hidden h-11 w-11 rounded-full dark:block" />
            <div>
              <p className="text-lg font-bold leading-tight text-slate-800 dark:text-white">{CLUB.name}</p>
              <p className="text-xs font-medium text-slate-500 dark:text-slate-400">{CLUB.fullName}</p>
            </div>
          </div>
          <p className="mt-4 text-sm italic text-slate-500 dark:text-slate-400">{CLUB.tagline}</p>
          <div className="mt-5">
            <SocialButtons variant="light" testIdPrefix="footer" />
          </div>
        </div>

        <div className="md:col-span-7 lg:col-span-4">
          <p className={HEADING}>Liên hệ</p>
          <ul className="space-y-3 text-sm text-slate-600 dark:text-slate-300">
            <li className="flex items-start gap-2.5">
              <Mail size={16} className="mt-0.5 shrink-0 text-brand-500" />
              <a href={`mailto:${CLUB.email}`} className={`-my-1 inline-block break-all py-1 ${LINK}`}>
                {CLUB.email}
              </a>
            </li>
            <li className="flex items-start gap-2.5">
              <MapPin size={16} className="mt-0.5 shrink-0 text-brand-500" />
              <address className="not-italic leading-relaxed">
                {CLUB.addressLines[0]}
                <br />
                <span className="text-slate-500 dark:text-slate-400">{CLUB.addressLines[1]}</span>
              </address>
            </li>
          </ul>
        </div>

        <nav aria-label="Liên kết cuối trang" className="md:col-span-5 lg:col-span-3">
          <p className={HEADING}>Liên kết</p>
          <ul className="grid grid-cols-2 gap-x-4 text-sm text-slate-600 md:grid-cols-1 dark:text-slate-300">
            {links.map((l) => (
              <li key={l.label}>
                <Link to={l.to} viewTransition={l.to === publicPaths.competition} className={`inline-block py-1.5 ${LINK}`}>
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </div>

      <div className="border-t border-slate-200 dark:border-navy-800">
        <p className="mx-auto w-full max-w-[1328px] px-4 py-5 text-center text-xs text-slate-500 md:px-6 dark:text-slate-400">
          © {new Date().getFullYear()} {CLUB.fullName}
        </p>
      </div>
    </footer>
  );
}
